/** Pure helpers for the Add / edit model form. */

import {
  AiModelIn as AiModelInSchema,
  aiModelInDisplayNameMax,
  aiModelInMaxOutputTokensMax,
  aiModelInMaxOutputTokensMin,
  aiModelInModelIdMax,
  aiModelInPriceInputOneMax,
} from '@/api/generated/schemas';
import type { AiModel, AiModelInput, AuthMethod, Hosting, ProviderField } from '@/api/types';
import { fieldErrors, oneOf, type FieldErrors } from '@/api/validate';

export const MIN_OUTPUT_TOKENS = aiModelInMaxOutputTokensMin;
export const MAX_OUTPUT_TOKENS = aiModelInMaxOutputTokensMax;
export const DISPLAY_NAME_LIMIT = aiModelInDisplayNameMax;
export const MODEL_ID_LIMIT = aiModelInModelIdMax;
/** Every rate shares the API's bound. */
export const PRICE_LIMIT = aiModelInPriceInputOneMax;

export type PriceKey = 'price_input' | 'price_output' | 'price_cache_write' | 'price_cache_read';

export function defaultsFor(fields: ProviderField[]): Record<string, string> {
  return Object.fromEntries(fields.filter((f) => f.default).map((f) => [f.key, f.default as string]));
}

/** Mirrors backend vendor_for() closely enough to filter auth methods live. */
export function guessVendor(hosting: string, modelId: string, config: Record<string, string>): string {
  const id = modelId.trim().toLowerCase();
  if (hosting === 'anthropic') return 'anthropic';
  if (hosting === 'azure_foundry') return config.apiFormat === 'anthropic' || id.startsWith('claude') ? 'anthropic' : 'openai';
  return 'other';
}

export function authMethodsFor(hosting: Hosting, vendor: string): AuthMethod[] {
  return hosting.auth.filter((a) => a.vendors.length === 0 || a.vendors.includes(vendor));
}

function formFromModel(model: AiModel): AiModelInput {
  return {
    display_name: model.display_name,
    hosting: model.hosting,
    capability: oneOf(AiModelInSchema.shape.capability, model.capability, 'chat'),
    model_id: model.model_id,
    auth: model.auth,
    config: Object.fromEntries(Object.entries(model.config).map(([k, v]) => [k, String(v)])),
    max_output_tokens: model.max_output_tokens,
    price_input: model.price_input ?? '',
    price_output: model.price_output ?? '',
    price_cache_write: model.price_cache_write ?? '',
    price_cache_read: model.price_cache_read ?? '',
    enabled: model.enabled,
    sort_order: model.sort_order,
  };
}

/** A hosting's first auth method and its default provider settings. */
export function hostingDefaults(hosting: Hosting): Pick<AiModelInput, 'hosting' | 'auth' | 'config'> {
  return { hosting: hosting.id, auth: hosting.auth[0]?.id ?? '', config: defaultsFor(hosting.fields) };
}

function blankForm(hosting: Hosting): AiModelInput {
  return {
    display_name: '',
    capability: 'chat',
    model_id: '',
    ...hostingDefaults(hosting),
    max_output_tokens: '',
    price_input: '',
    price_output: '',
    price_cache_write: '',
    price_cache_read: '',
    enabled: true,
    sort_order: 0,
  };
}

export function initialForm(model: AiModel | undefined, firstHosting: Hosting): AiModelInput {
  return model ? formFromModel(model) : blankForm(firstHosting);
}

/** What a person types into the dialog, checked by the API's own validator. */
const TYPED = AiModelInSchema.pick({
  display_name: true,
  model_id: true,
  max_output_tokens: true,
  price_input: true,
  price_output: true,
  price_cache_write: true,
  price_cache_read: true,
});

/** The typed fields: each shows its own 422 message beside it, and the banner carries the rest. */
export type TypedField = keyof typeof TYPED.shape;
export const TYPED_FIELDS = Object.keys(TYPED.shape) as TypedField[];

const asPrice = (v: string) => (v.trim() === '' || Number.isNaN(Number(v)) ? v : Number(v));

/** The form's problems by field; empty when Test may run. */
export function formErrors(form: AiModelInput): FieldErrors {
  return fieldErrors(TYPED, {
    ...form,
    price_input: asPrice(form.price_input),
    price_output: asPrice(form.price_output),
    price_cache_write: asPrice(form.price_cache_write),
    price_cache_read: asPrice(form.price_cache_read),
  });
}
